import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, SearchOutlined, ReloadOutlined, LoadingOutlined } from '@ant-design/icons';
import ProjectCard from '../../components/ui/ProjectCard';
import EmptyState from '../../components/shared/EmptyState/EmptyState';
import { servicesService } from '../../services';
import { AppDeployService } from '../../types/service.type';
import { getServiceAge } from '../../utils/serviceHelpers';
import { getTechnologyImage } from '../../utils/serviceIconMapper';
import { useTranslation } from 'react-i18next';

interface DeployApp {
  id: string;
  name: string;
  status: 'running' | 'stopped' | 'deploying' | 'error';
  lastDeploy: string;
  type: string;
  framework?: string;
  url?: string;
  repository?: string;
  publicUrl?: string;
  technologyImage?: string;
}

const DeployApps: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'running' | 'stopped' | 'deploying' | 'error' | 'all'>('all');
  const [deployApps, setDeployApps] = useState<DeployApp[]>([]);
  const [loading, setLoading] = useState(true);

  const isAppDeployService = (service: any): boolean => {
    if (!service || !service.type) return false;
    const type = service.type.toUpperCase();
    return type === 'APP_DEPLOY' || type === 'APP_DEPLOY_STATIC' || type === 'APP_DEPLOY_SERVER' || type.startsWith('APP_DEPLOY');
  };

  const mapAppDeployServiceToDeployApp = useCallback((serviceData: any): DeployApp => {
    const mapStatus = (status: string): 'running' | 'stopped' | 'deploying' | 'error' => {
      switch (status?.toLowerCase()) {
        case 'running': return 'running';
        case 'stopped': return 'stopped';
        case 'deploying':
        case 'initializing':
        case 'stacking':
        case 'creating':
        case 'starting':
        case 'restarting': return 'deploying';
        case 'error':
        case 'terminated': return 'error';
        default: return 'stopped';
      }
    };
    const metadata = serviceData.metadata || {};
    const appConfig = metadata.appConfig || {};
    const technology = metadata.technology || appConfig.type || serviceData.type || 'Unknown';
    const framework = metadata.technology || appConfig.type || technology;
    const updatedAt = serviceData.updatedAt
      ? (typeof serviceData.updatedAt === 'string' ? new Date(serviceData.updatedAt) : serviceData.updatedAt)
      : new Date();
    const createdAt = serviceData.createdAt
      ? (typeof serviceData.createdAt === 'string' ? new Date(serviceData.createdAt) : serviceData.createdAt)
      : new Date();
    return {
      id: serviceData.id,
      name: serviceData.name || 'Unnamed App',
      status: mapStatus(serviceData.status),
      lastDeploy: getServiceAge(updatedAt || createdAt),
      type: technology,
      framework,
      url: serviceData.publicUrl || serviceData.primaryUrl,
      publicUrl: serviceData.publicUrl,
      repository: undefined,
      technologyImage: getTechnologyImage(technology),
    };
  }, []);

  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const response = await servicesService.getByCategory('APP_DEPLOY');
      const mappedApps = response.services.filter(isAppDeployService).map(mapAppDeployServiceToDeployApp);
      setDeployApps(mappedApps);
    } catch {
      setDeployApps([]);
    } finally {
      setLoading(false);
    }
  }, [mapAppDeployServiceToDeployApp]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const filteredApps = deployApps.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (app.framework && app.framework.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (app.url && app.url.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const STATUS_PILLS = [
    { value: 'all', label: t('common.all') || 'Todas' },
    { value: 'running', label: t('deployApp.list.running') },
    { value: 'stopped', label: t('deployApp.list.stopped') },
    { value: 'deploying', label: t('deployApp.list.deploying') },
    { value: 'error', label: t('deployApp.list.error') || 'Error' },
  ];

  return (
    <div className="max-w-[1600px] mx-auto px-8 py-4">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('deployApp.list.title')}
          </h1>
          {!loading && (
            <span className="text-sm text-gray-400 dark:text-gray-500">{filteredApps.length}</span>
          )}
        </div>

        <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
          {/* Search */}
          <div className="relative">
            <SearchOutlined className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs pointer-events-none" />
            <input
              type="text"
              placeholder={t('deployApp.list.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:focus:ring-gray-400 w-44"
            />
          </div>

          {/* Status pills */}
          <div className="flex items-center gap-1">
            {STATUS_PILLS.map(({ value, label }) => (
              <button
                key={value}
                onClick={() => setFilterStatus(value as typeof filterStatus)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  filterStatus === value
                    ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white'
                    : 'text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            onClick={fetchApps}
            disabled={loading}
            className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors disabled:opacity-40"
            title={t('common.refresh')}
          >
            {loading ? <LoadingOutlined spin /> : <ReloadOutlined />}
          </button>

          {/* Docs link */}
          <a
            href="https://docs.kubiy.com/app-deploy"
            target="_blank"
            rel="noopener noreferrer"
            className="px-2.5 py-1 text-xs text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-md hover:border-gray-400 dark:hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            Documentación
          </a>

          {/* Create */}
          <button
            onClick={() => navigate('/deploy-apps/create')}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm rounded-md hover:bg-gray-700 dark:hover:bg-gray-100 transition-colors"
          >
            <PlusOutlined className="text-xs" />
            {t('deployApp.list.deployNewApp')}
          </button>
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-20">
          <LoadingOutlined className="text-2xl text-gray-300 dark:text-gray-600" spin />
        </div>
      ) : filteredApps.length === 0 ? (
        <EmptyState
          title={t('deployApp.list.noAppsFound')}
          description={
            searchTerm || filterStatus !== 'all'
              ? t('deployApp.list.noAppsFilterDesc')
              : t('deployApp.list.noAppsEmptyDesc')
          }
          action={
            !searchTerm && filterStatus === 'all'
              ? { label: t('deployApp.list.deployNewApp'), onClick: () => navigate('/deploy-apps/create') }
              : undefined
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredApps.map((app) => (
            <ProjectCard
              key={app.id}
              id={app.id}
              name={app.name}
              status={app.status}
              lastDeploy={app.lastDeploy}
              type={app.type}
              url={app.url}
              technologyImage={app.technologyImage}
              onRefresh={fetchApps}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DeployApps;
